import React, { useEffect, useMemo, useState } from "react";
import { Results as ResultList, useSearch } from "@usereactify/search";
import { useProntoContext } from "../../providers/pronto";

import { useResults } from "@usereactify/search";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSearchArticles } from "../../hooks/useSearchArticles";
import { DesktopFilterBar, MobileFilterBar } from "../components/Facets/facets";
import { ProductCard, InstantSearchCard } from "../components/Cards/cards";
import { ResultsLoading } from "./resultsLoading";
import { ResultPaginationLoadMore } from "./result-pagination-load-more";
import Moment from "moment";

export const Results: React.FC<ResultsProps> = ({ activeTab = "products", data }) => {
  const resultsHook = useResults();
  const { pronto, setPronto } = useProntoContext();
  const { isMobile } = useIsMobile();
  const searchResults = useSearchArticles();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number | undefined>();
  const [promoItems, setPromoItems] = useState<any>(null);
  const createDate = (article: Article) => {
    Moment.locale("en");
    return <div> {Moment(article.published_at).format("MMMM d YYYY")} </div>;
  };

  useEffect(() => {
    if (window !== undefined && data && data?.handle !== undefined) {
      const lastPageSize = localStorage.getItem("salt-and-pepper:lastPageSize");
      const currentTime = new Date().getTime();
      try {
        const pageSizeObj = JSON.parse(lastPageSize!);

        if (pageSizeObj?.collection === data.handle && currentTime - pageSizeObj.clickedTime < 300000) {
          setPageSize(pageSizeObj?.size);
        } else {
          setPageSize(resultsHook?.resultStats?.displayedResults);
        }
      } catch (error) {
        throw new Error("last scroll position object parse error");
      }
    }
  }, [data?.handle]);

  useMemo(() => {
    if (!resultsHook?.results.length) return;
    let baseUrl = "https://saltandpepper.com.au/apps/connector";
    if (Shopify?.shop.includes('staging')) {
      baseUrl = "https://salt-and-pepper-au-staging.myshopify.com/apps/connector";
    }

    const offset = pageSize ?? 24;

    const nextPageItems = resultsHook?.results.splice(offset * -1, offset);

    const items = nextPageItems?.map(({ price_min, variants }) => {
      return {
        sku: variants[0]?.sku,
        price: Number((Number(price_min) * 100).toFixed(0)),
      };
    });
    const fetchPromoItem = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseUrl}/api-promotionMarkdown`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items: items }),
        });
        const { data } = await response.json();
        if (data?.items) {
          setPromoItems(data?.items);
          setPronto((current) => [...current, ...data?.items]);
        }
      } catch (e) {
        console.error((e as Error).message);
      }
      setLoading(false);
    };
    if (!loading) {
      fetchPromoItem();
    }
  }, [resultsHook?.results, setPronto, setPromoItems]);

  useEffect(() => {
    document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
  }, [resultsHook?.results, loading]);

  return (
    <>
      <MobileFilterBar />
      <div className="row">
        <div className="small-12 columns">
          {activeTab === "products" && <DesktopFilterBar />}
          <div className="search-page-results">
            <div className={`thb-quick-search--results ${activeTab === "products" ? "active" : ""}`}>
              <ResultList
                includeFields={["*"]}
                listClassName="products collection row small-up-2 medium-up-4"
                renderResultCardProduct={(props) => (
                  <ProductCard
                    props={props}
                    promoItem={pronto?.filter(({ sku }) => sku === props?.product?.variants[0]?.sku)[0] || null}
                  />
                )}
                {...(pageSize && { pageSize: pageSize })}
                listStyle={{
                  gap: "30px",
                  gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
                  display: "grid",
                }}
                renderLoading={() => <ResultsLoading />}
                renderPaginationLoadMore={(renderProps) => (
                  <ResultPaginationLoadMore handle={data?.handle} {...renderProps} />
                )}
              />
            </div>
            <div className={`thb-quick-search--results ${activeTab === "articles" ? "active" : ""}`}>
              <div className=" row small-up-2 medium-up-4 grid-search-page">
                {searchResults?.resources?.results?.articles?.map((article: Article) => (
                  <div className="product-card text-center">
                    <figure className="product-featured-image thb-hover">
                      <a
                        href={article.url}
                        title={article.title}
                        className="product-featured-image-link aspect-ratio aspect-ratio--portrait"
                      >
                        <img
                          className="lazyautosizes ls-is-cached lazyloaded"
                          width="291"
                          height="291"
                          data-sizes="auto"
                          src={article.image}
                          data-srcset={article.image}
                          alt={article.title}
                          sizes="100px"
                          loading="lazy"
                        />
                      </a>
                    </figure>
                    <div className="article-card">
                      <div>
                        <div className="fs-small article-date">{createDate(article)}</div>
                      </div>
                      <a href={article.url} title={article.title} className="article-link fw-bold fs-large">
                        {article.title}
                      </a>
                      <a href={article.url} title={article.title} className="button black secondary">
                        {` Read More`}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const InstantSearchResults: React.FC = () => {
  const { isMobile } = useIsMobile();
  const searchResults = useSearchArticles();
  const { searchTerm } = useSearch();
  const [active, setActive] = useState("tab1");

  return searchTerm ? (
    <div className="thb-quick-search--results-container">
      <div className="thb-quick-search--categories-container">
        <span
          className={`fs-large search-categories ${active === "tab1" ? "active fw-bold" : ""}`}
          onClick={() => setActive("tab1")}
        >
          Products
        </span>
        <span
          className={`fs-large search-categories ${active === "tab2" ? "active fw-bold" : ""}`}
          onClick={() => setActive("tab2")}
        >
          Articles
        </span>
      </div>
      <div className={`thb-quick-search--results ${active === "tab1" ? "active" : ""}`}>
        <ResultList
          includeFields={["*"]}
          pageSize={isMobile ? 4 : 6}
          listClassName="grid-search-results"
          listStyle={{}}
          renderLoading={() => null}
          renderPaginationNextPrev={() => null}
          renderResultCardProduct={InstantSearchCard}
          // renderResultCardProduct={(props) => <ProductCard props={props} promoItem={promoItems?.filter(({ sku }) => sku === props?.product?.variants[0]?.sku)[0] || null} />}
          renderNoResults={() => <div className="grid-search-results">No results</div>}
        />
      </div>
      <div className={`thb-quick-search--results ${active === "tab2" ? "active" : ""}`}>
        <div className="grid-search-results">
          {searchResults ? (
            searchResults?.resources?.results?.articles?.map((article: Article) => (
              <div className="product-cart-item fs-large">
                <a href={article.url}>
                  <div className="product-cart-item-image">
                    <img
                      className="lazyautosizes ls-is-cached lazyloaded"
                      width="107"
                      height="107"
                      data-sizes="auto"
                      src={article.featured_image.url}
                      data-srcset={article.featured_image.url}
                      alt={article.title}
                      sizes="100px"
                      loading="lazy"
                    />
                  </div>
                </a>
                <div>
                  <a href={article.url} title={article.title} className="cart-article-link">
                    {article.title}
                  </a>
                </div>
              </div>
            ))
          ) : (
            <div className="grid-search-results">No results</div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

interface Article {
  author: string;
  body: string;
  featured_image: {
    alt: string;
    aspect_ratio: number;
    height: number;
    url: string;
    width: number;
  };
  handle: string;
  id: number;
  image: string;
  published_at: string;
  summary_html: string;
  tags: [];
  title: string;
  url: string;
}

interface ResultsProps {
  activeTab?: string;
  data: any;
}

interface Metafield {
  admin_graphql_api_id: string;
  created_at: string;
  description: string;
  id: number;
  key: string;
  namespace: string;
  owner_id: number;
  owner_resource: string;
  type: string;
  updated_at: string;
  value: string;
}
